import httpService from "./httpService";

export type AggregationGranularity = "daily" | "weekly" | "monthly" | "yearly" | "latest";
export type AggregateProductType = "BASKET" | "PORTFOLIO";

export interface AggregateResponse<T> {
  items: T[];
}

export interface ProviderAggregateItem {
  period: string;
  numOfUsers: number;
  totalAum: number;
  providerShare?: number | null;
}

export interface PlatformAggregateItem {
  period: string;
  numOfProviders: number;
  numOfUsers: number;
  totalAum: number;
  numOfUsersWithMultiProductStrategies: number;
}

export interface AllProviderAggregateItem extends ProviderAggregateItem {
  providerId: number;
}

export interface ProductAggregateItem {
  period: string;
  productType: AggregateProductType;
  productId: number;
  numOfUsers: number;
  totalAum: number;
}

export function getDashboardTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

function buildAggregateQuery(aggregation: AggregationGranularity): string {
  const params = new URLSearchParams({
    timezone: getDashboardTimezone(),
    aggregation,
  });
  return `?${params.toString()}`;
}

class AggregatesService {
  async getProviderAggregates(
    aggregation: AggregationGranularity
  ): Promise<ProviderAggregateItem[]> {
    const response = await httpService.get<AggregateResponse<ProviderAggregateItem>>(
      `/v3/providers/aggregates${buildAggregateQuery(aggregation)}`
    );
    return response.data.items ?? [];
  }

  async getProviderProductAggregates(
    aggregation: AggregationGranularity
  ): Promise<ProductAggregateItem[]> {
    const response = await httpService.get<AggregateResponse<ProductAggregateItem>>(
      `/v3/providers/products/aggregates${buildAggregateQuery(aggregation)}`
    );
    return response.data.items ?? [];
  }

  async getAdminPlatformAggregates(
    aggregation: AggregationGranularity
  ): Promise<PlatformAggregateItem[]> {
    const response = await httpService.get<AggregateResponse<PlatformAggregateItem>>(
      `/v3/admin/aggregates${buildAggregateQuery(aggregation)}`
    );
    return response.data.items ?? [];
  }

  async getAdminAllProviderAggregates(
    aggregation: AggregationGranularity
  ): Promise<AllProviderAggregateItem[]> {
    const response = await httpService.get<AggregateResponse<AllProviderAggregateItem>>(
      `/v3/admin/aggregates/providers${buildAggregateQuery(aggregation)}`
    );
    return response.data.items ?? [];
  }

  async getAdminProviderAggregates(
    providerId: number,
    aggregation: AggregationGranularity
  ): Promise<ProviderAggregateItem[]> {
    const response = await httpService.get<AggregateResponse<ProviderAggregateItem>>(
      `/v3/admin/aggregates/providers/${providerId}${buildAggregateQuery(aggregation)}`
    );
    return response.data.items ?? [];
  }

  async getAdminProviderProductAggregates(
    providerId: number,
    aggregation: AggregationGranularity
  ): Promise<ProductAggregateItem[]> {
    const response = await httpService.get<AggregateResponse<ProductAggregateItem>>(
      `/v3/admin/aggregates/providers/${providerId}/products${buildAggregateQuery(aggregation)}`
    );
    return response.data.items ?? [];
  }
}

export const aggregatesService = new AggregatesService();
